/* External Deps */
import { jsPDF } from 'jspdf';

/* Internal Deps */

// Style constants for consistent formatting
const STYLES = {
  // Colors
  colors: {
    black: '#000000',
    white: '#ffffff',
    headerBg: '#f0f0f0',
    borderColor: '#000000',
  },
  
  // Font sizes (in points)
  fontSize: {
    mainTitle: 16,
    subtitle: 10,
    sectionTitle: 10,
    content: 9,
    tableHeader: 8,
    tableContent: 8,
    small: 8,
  },
  
  // Spacing
  spacing: {
    margin: 40,
    sectionPadding: 12,
    lineHeight: 15,
    smallLineHeight: 10,
    paragraphGap: 10,
  },
  
  // Dimensions
  dimensions: {
    checkboxSize: 10,
    borderWidth: 0.5,
  }
};

interface PageState {
  currentY: number;
  pageNumber: number;
  pageHeight: number;
  pageWidth: number;
  contentWidth: number;
  margin: number;
}

// Enhanced text-selectable PDF generator with professional styling
export class EnhancedTextSelectablePDFGenerator {
  private static doc: jsPDF;
  private static pageState: PageState;

  static async generatePDF(certificateData: FullCertificateData): Promise<void> {
    try {
      const { extractedData, _id } = certificateData;
      
      // Initialize PDF document
      this.doc = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: 'letter'
      });

      // Initialize page state
      this.pageState = {
        currentY: STYLES.spacing.margin + 20,
        pageNumber: 1,
        pageHeight: this.doc.internal.pageSize.getHeight(),
        pageWidth: this.doc.internal.pageSize.getWidth(),
        contentWidth: this.doc.internal.pageSize.getWidth() - (STYLES.spacing.margin * 2),
        margin: STYLES.spacing.margin,
      };

      // Set default font
      this.doc.setFont('helvetica');
      
      // Render header
      this.renderHeader();
      
      // Render sections
      this.renderCertifierSection(extractedData);
      this.renderCertifierDetailsSection(extractedData);
      this.renderExporterDetailsSection(extractedData);
      this.renderProducerDetailsSection(extractedData);
      this.renderImporterDetailsSection(extractedData);
      
      // Render items table with page break handling
      this.renderItemsTable(extractedData);
      
      // Render remaining sections
      this.renderPeriodCoverageSection();
      this.renderCertificationSection();

      // Save the PDF
      const filename = `CUSMA_Certificate_${_id || 'Document'}.pdf`;
      this.doc.save(filename);
      
    } catch (error) {
      console.error('Error generating enhanced text-selectable PDF:', error);
      throw error;
    }
  }

  // Helper method to check if we need a new page
  private static checkPageBreak(requiredHeight: number): void {
    if (this.pageState.currentY + requiredHeight > this.pageState.pageHeight - STYLES.spacing.margin) {
      this.addNewPage();
    }
  }

  // Add a new page and update state
  private static addNewPage(): void {
    this.doc.addPage();
    this.pageState.pageNumber++;
    this.pageState.currentY = STYLES.spacing.margin;
  }

  // Render professional checkbox
  private static renderCheckbox(x: number, y: number, checked: boolean): void {
    const size = STYLES.dimensions.checkboxSize;
    
    // Draw checkbox border
    this.doc.setLineWidth(STYLES.dimensions.borderWidth);
    this.doc.rect(x, y - size + 2, size, size);
    
    // Draw checkmark if checked
    if (checked) {
      this.doc.setLineWidth(1.5);
      // Draw checkmark
      this.doc.line(x + 2, y - size/2 + 2, x + size/3, y - 2);
      this.doc.line(x + size/3, y - 2, x + size - 2, y - size + 4);
    }
  }

  // Render header with proper styling
  private static renderHeader(): void {
    const { currentY } = this.pageState;
    
    // Main title
    this.doc.setFontSize(STYLES.fontSize.mainTitle);
    this.doc.setFont('helvetica', 'bold');
    
    const mainTitle = 'CUSMA / USMCA / T-MEC CERTIFICATION OF ORIGIN';
    const titleWidth = this.doc.getTextWidth(mainTitle);
    const titleX = (this.pageState.pageWidth - titleWidth) / 2;
    
    this.doc.text(mainTitle, titleX, currentY);
    this.pageState.currentY += 25;
    
    // Subtitles
    this.doc.setFontSize(STYLES.fontSize.subtitle);
    this.doc.setFont('helvetica', 'italic');
    
    const subtitles = [
      'Canada – United States – Mexico Agreement',
      'United States – Mexico – Canada Agreement',
      'Tratado entre México, Estados Unidos y Canadá'
    ];
    
    subtitles.forEach(subtitle => {
      const subtitleWidth = this.doc.getTextWidth(subtitle);
      const subtitleX = (this.pageState.pageWidth - subtitleWidth) / 2;
      this.doc.text(subtitle, subtitleX, this.pageState.currentY);
      this.pageState.currentY += 12;
    });
    
    // Header border line
    this.pageState.currentY += 5;
    this.doc.setLineWidth(2);
    this.doc.line(
      this.pageState.margin, 
      this.pageState.currentY, 
      this.pageState.pageWidth - this.pageState.margin, 
      this.pageState.currentY
    );
    this.pageState.currentY += 20;
  }

  // Render bordered section with consistent styling
  private static renderBorderedSection(
    title: string, 
    content: string[], 
    minHeight = 60
  ): void {
    const lineHeight = STYLES.spacing.lineHeight;
    const padding = STYLES.spacing.sectionPadding;
    const calculatedHeight = Math.max(minHeight, content.length * lineHeight + padding * 2 + 20);
    
    // Check if we need a new page
    this.checkPageBreak(calculatedHeight);
    
    const startY = this.pageState.currentY;
    
    // Draw border
    this.doc.setLineWidth(STYLES.dimensions.borderWidth);
    this.doc.rect(this.pageState.margin, startY, this.pageState.contentWidth, calculatedHeight);
    
    // Add title
    this.doc.setFontSize(STYLES.fontSize.sectionTitle);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(title, this.pageState.margin + padding, startY + 15);
    
    // Add content
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(STYLES.fontSize.content);
    
    let contentY = startY + 30;
    const maxWidth = this.pageState.contentWidth - (padding * 3);
    
    content.forEach(line => {
      if (contentY + lineHeight < startY + calculatedHeight - 5) {
        // Wrap text if needed
        const wrappedLines = this.wrapText(line, maxWidth);
        wrappedLines.forEach(wrappedLine => {
          if (contentY + lineHeight < startY + calculatedHeight - 5) {
            this.doc.text(wrappedLine, this.pageState.margin + padding * 2, contentY);
            contentY += lineHeight;
          }
        });
      }
    });
    
    this.pageState.currentY = startY + calculatedHeight;
  }

  // Text wrapping helper
  private static wrapText(text: string, maxWidth: number): string[] {
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';
    
    words.forEach(word => {
      const testLine = currentLine + (currentLine ? ' ' : '') + word;
      const textWidth = this.doc.getTextWidth(testLine);
      
      if (textWidth > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    });
    
    if (currentLine) {
      lines.push(currentLine);
    }
    
    return lines;
  }

  // Render certifier section with checkboxes
  private static renderCertifierSection(extractedData: any): void {
    const startY = this.pageState.currentY;
    const sectionHeight = 50;
    
    this.checkPageBreak(sectionHeight);
    
    // Draw border
    this.doc.setLineWidth(STYLES.dimensions.borderWidth);
    this.doc.rect(this.pageState.margin, startY, this.pageState.contentWidth, sectionHeight);
    
    // Add title
    this.doc.setFontSize(STYLES.fontSize.sectionTitle);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('1. CERTIFIER', this.pageState.margin + STYLES.spacing.sectionPadding, startY + 15);
    
    // Add checkboxes
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(STYLES.fontSize.content);
    
    const checkboxY = startY + 35;
    const checkboxSpacing = 120;
    
    // Importer checkbox
    this.renderCheckbox(this.pageState.margin + 20, checkboxY, extractedData.certifier === 'Importer');
    this.doc.text('IMPORTER', this.pageState.margin + 35, checkboxY);
    
    // Exporter checkbox
    this.renderCheckbox(this.pageState.margin + 20 + checkboxSpacing, checkboxY, extractedData.certifier === 'Exporter');
    this.doc.text('EXPORTER', this.pageState.margin + 35 + checkboxSpacing, checkboxY);
    
    // Producer checkbox
    this.renderCheckbox(this.pageState.margin + 20 + checkboxSpacing * 2, checkboxY, extractedData.certifier === 'Producer');
    this.doc.text('PRODUCER', this.pageState.margin + 35 + checkboxSpacing * 2, checkboxY);
    
    this.pageState.currentY = startY + sectionHeight;
  }

  // Render other sections
  private static renderCertifierDetailsSection(extractedData: any): void {
    const certDetails = extractedData.certifierDetails || {};
    const content = [
      `YOUR NAME: ${certDetails.yourName || '_________________________________________________________'}`,
      `TITLE: ${certDetails.title || '_____________________________________________________________'}`,
      `COMPANY NAME: ${certDetails.companyName || '___________________________________________________'}`,
      `ADDRESS: ${certDetails.address || '___________________________________________________________'}`,
      `COUNTRY & POSTAL/ZIP CODE: ${certDetails.countryAndPostalCode || '_________________________________________'}`,
      `PHONE: ${certDetails.phone || '________________________'}    EMAIL: ${certDetails.email || '__________________________'}`
    ];
    this.renderBorderedSection('2. CERTIFIER DETAILS', content, 110);
  }

  private static renderExporterDetailsSection(extractedData: any): void {
    const content = [
      `COMPANY NAME: ${extractedData.exporterDetails?.companyName || '________________________________________________'}`,
      `ADDRESS: ${extractedData.exporterDetails?.address || '______________________________________________________'}`,
      `COUNTRY & POSTAL/ZIP CODE: ${extractedData.exporterDetails?.countryAndPostalCode || '____________________________________'}`,
      `PHONE: ${extractedData.exporterDetails?.phone || '_______________________'}`,
      `EMAIL: ${extractedData.exporterDetails?.email || '_______________________________________________'}`
    ];
    this.renderBorderedSection('3. EXPORTER DETAILS (If different than Certifier)', content, 110);
  }

  private static renderProducerDetailsSection(extractedData: any): void {
    const prodDetails = extractedData.producerDetails;
    let content: string[];
    
    if (prodDetails && (prodDetails.companyName || prodDetails.address || prodDetails.phone || prodDetails.email)) {
      content = [
        `COMPANY NAME: ${prodDetails.companyName || '________________________________________________'}`,
        `ADDRESS: ${prodDetails.address || '______________________________________________________'}`,
        `COUNTRY & POSTAL/ZIP CODE: ${prodDetails.countryAndPostalCode || '____________________________________'}`,
        `PHONE: ${prodDetails.phone || '_______________________'}`,
        `EMAIL: ${prodDetails.email || '_______________________________________________'}`
      ];
    } else {
      content = [
        'COMPANY NAME: Same as exporter',
        'ADDRESS: Same as exporter',
        'COUNTRY & POSTAL/ZIP CODE: Same as exporter',
        'PHONE: Same as exporter',
        'EMAIL: Same as exporter'
      ];
    }
    this.renderBorderedSection('4. PRODUCER DETAILS', content, 100);
  }

  private static renderImporterDetailsSection(extractedData: any): void {
    const content = [
      `COMPANY NAME: ${extractedData.importerDetails?.companyName || '________________________________________________'}`,
      `ADDRESS: ${extractedData.importerDetails?.address || '______________________________________________________'}`,
      `COUNTRY & POSTAL/ZIP CODE: ${extractedData.importerDetails?.countryAndPostalCode || '____________________________________'}`,
      `PHONE: ${extractedData.importerDetails?.phone || '________________________'}    EMAIL: ${extractedData.importerDetails?.email || '__________________________'}`
    ];
    this.renderBorderedSection('5. IMPORTER DETAILS (If known)', content, 110);
  }

  // Render items table with advanced page break handling
  private static renderItemsTable(extractedData: any): void {
    // Filter to only include items where INCLUDED === true
    const items = (extractedData.items || []).filter((item: any) => item.INCLUDED);
    const rowHeight = 45;
    
    // Column widths
    const col1Width = this.pageState.contentWidth * 0.2;
    const col2Width = this.pageState.contentWidth * 0.6;
    const col3Width = this.pageState.contentWidth * 0.2;
    
    // Start table
    let tableStartY = this.pageState.currentY;
    let currentItemIndex = 0;
    let isFirstPage = true;
    
    while (currentItemIndex < items.length || (items.length === 0 && isFirstPage)) {
      // Check if we need headers
      if (isFirstPage || this.pageState.currentY === STYLES.spacing.margin) {
        // Draw table header
        this.renderTableHeader(tableStartY, col1Width, col2Width, col3Width, !isFirstPage);
        tableStartY = this.pageState.currentY;
        isFirstPage = false;
      }
      
      // Render items
      if (items.length === 0) {
        // Empty table row
        this.renderEmptyTableRow(col1Width, col2Width, col3Width);
        break;
      } else {
        // Render actual items
        while (currentItemIndex < items.length) {
          // Check if we have space for another row
          if (this.pageState.currentY + rowHeight > this.pageState.pageHeight - STYLES.spacing.margin) {
            // Need a new page
            this.addNewPage();
            tableStartY = this.pageState.currentY;
            break; // Break inner loop to render headers on new page
          }
          
          // Render item row
          this.renderItemRow(items[currentItemIndex], col1Width, col2Width, col3Width);
          currentItemIndex++;
        }
      }
    }
    
    // Add some spacing after table
    this.pageState.currentY += 15;
  }

  // Render table header with continuation support
  private static renderTableHeader(
    startY: number, 
    col1Width: number, 
    col2Width: number, 
    col3Width: number,
    isContinuation: boolean
  ): void {
    const headerHeight = 50;
    
    // Section border and title
    this.doc.setLineWidth(STYLES.dimensions.borderWidth);
    this.doc.rect(this.pageState.margin, startY, this.pageState.contentWidth, headerHeight);
    
    this.doc.setFontSize(STYLES.fontSize.sectionTitle);
    this.doc.setFont('helvetica', 'bold');
    const title = '6a, 6b & 7. GOODS DESCRIPTION AND ORIGIN CRITERIA' + (isContinuation ? ' (continued)' : '');
    this.doc.text(title, this.pageState.margin + STYLES.spacing.sectionPadding, startY + 15);
    
    // Table header row
    const tableY = startY + 30;
    
    // Header cells with background
    this.doc.setFillColor(240, 240, 240);
    this.doc.rect(this.pageState.margin, tableY, col1Width, 40, 'F');
    this.doc.rect(this.pageState.margin + col1Width, tableY, col2Width, 40, 'F');
    this.doc.rect(this.pageState.margin + col1Width + col2Width, tableY, col3Width, 40, 'F');
    
    // Header borders
    this.doc.rect(this.pageState.margin, tableY, col1Width, 40);
    this.doc.rect(this.pageState.margin + col1Width, tableY, col2Width, 40);
    this.doc.rect(this.pageState.margin + col1Width + col2Width, tableY, col3Width, 40);
    
    // Header text
    this.doc.setFontSize(STYLES.fontSize.tableHeader);
    this.doc.setFont('helvetica', 'bold');
    
    // Column 1 header
    const header1Lines = [
      '6a. HS TARIFF',
      'CLASSIFICATION',
      '(6-digit level)'
    ];
    header1Lines.forEach((line, index) => {
      const textWidth = this.doc.getTextWidth(line);
      const x = this.pageState.margin + (col1Width - textWidth) / 2;
      this.doc.text(line, x, tableY + 12 + (index * 9));
    });
    
    // Column 2 header
    const header2Lines = [
      '6b. DESCRIPTION OF THE GOOD',
      'AND INVOICE NUMBER',
      '(If known)'
    ];
    header2Lines.forEach((line, index) => {
      const textWidth = this.doc.getTextWidth(line);
      const x = this.pageState.margin + col1Width + (col2Width - textWidth) / 2;
      this.doc.text(line, x, tableY + 12 + (index * 9));
    });
    
    // Column 3 header
    const header3Lines = [
      '7. ORIGIN CRITERION',
      'AS SET OUT IN',
      'ANNEX 4.2'
    ];
    header3Lines.forEach((line, index) => {
      const textWidth = this.doc.getTextWidth(line);
      const x = this.pageState.margin + col1Width + col2Width + (col3Width - textWidth) / 2;
      this.doc.text(line, x, tableY + 12 + (index * 9));
    });
    
    this.pageState.currentY = tableY + 40;
  }

  // Render item row
  private static renderItemRow(
    item: any, 
    col1Width: number, 
    col2Width: number, 
    col3Width: number
  ): void {
    const rowHeight = 45;
    const startY = this.pageState.currentY;
    
    // Draw cells
    this.doc.setLineWidth(STYLES.dimensions.borderWidth);
    this.doc.rect(this.pageState.margin, startY, col1Width, rowHeight);
    this.doc.rect(this.pageState.margin + col1Width, startY, col2Width, rowHeight);
    this.doc.rect(this.pageState.margin + col1Width + col2Width, startY, col3Width, rowHeight);
    
    // Cell content
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(STYLES.fontSize.tableContent);
    
    // Column 1 - HTS Code
    const htsCode = item.hts || '________';
    const htsCodeWidth = this.doc.getTextWidth(htsCode);
    const htsCodeX = this.pageState.margin + (col1Width - htsCodeWidth) / 2;
    this.doc.text(htsCode, htsCodeX, startY + 20);
    
    // Column 2 - Description
    const descriptionLines = [
      item.description || 'Item Description',
      `Part #: ${item.partNumber || '__________'}`,
      `Invoice: ${item.invoiceNumber || '__________'}`,
      `Quantity: ${item.ship || '__________'}`
    ];
    
    this.doc.setFontSize(STYLES.fontSize.tableContent);
    descriptionLines.forEach((line, index) => {
      // Bold for the main description
      if (index === 0) {
        this.doc.setFont('helvetica', 'bold');
      } else {
        this.doc.setFont('helvetica', 'normal');
        this.doc.setFontSize(STYLES.fontSize.small);
      }
      this.doc.text(line, this.pageState.margin + col1Width + 5, startY + 10 + (index * 9));
    });
    
    // Column 3 - Origin and Criterion
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(STYLES.fontSize.small);
    
    // Origin on first line
    const origin = `Origin: ${item.origin || 'N/A'}`;
    const originWidth = this.doc.getTextWidth(origin);
    const originX = this.pageState.margin + col1Width + col2Width + (col3Width - originWidth) / 2;
    this.doc.text(origin, originX, startY + 15);
    
    // Criterion on second line
    const criterion = `Criterion: ${item.originCriterion || 'N/A'}`;
    const criterionWidth = this.doc.getTextWidth(criterion);
    const criterionX = this.pageState.margin + col1Width + col2Width + (col3Width - criterionWidth) / 2;
    this.doc.text(criterion, criterionX, startY + 25);
    
    this.pageState.currentY = startY + rowHeight;
  }

  // Render empty table row
  private static renderEmptyTableRow(col1Width: number, col2Width: number, col3Width: number): void {
    const rowHeight = 40;
    const startY = this.pageState.currentY;
    
    // Draw cells
    this.doc.setLineWidth(STYLES.dimensions.borderWidth);
    this.doc.rect(this.pageState.margin, startY, col1Width, rowHeight);
    this.doc.rect(this.pageState.margin + col1Width, startY, col2Width, rowHeight);
    this.doc.rect(this.pageState.margin + col1Width + col2Width, startY, col3Width, rowHeight);
    
    // "No included items" text
    this.doc.setFont('helvetica', 'italic');
    this.doc.setFontSize(STYLES.fontSize.content);
    const text = 'No included items';
    const textWidth = this.doc.getTextWidth(text);
    const x = this.pageState.margin + col1Width + (col2Width - textWidth) / 2;
    this.doc.text(text, x, startY + 25);
    
    this.pageState.currentY = startY + rowHeight;
  }

  // Render period coverage section
  private static renderPeriodCoverageSection(): void {
    const startY = this.pageState.currentY;
    const sectionHeight = 50;
    
    this.checkPageBreak(sectionHeight);
    
    // Draw border
    this.doc.setLineWidth(STYLES.dimensions.borderWidth);
    this.doc.rect(this.pageState.margin, startY, this.pageState.contentWidth, sectionHeight);
    
    // Add title
    this.doc.setFontSize(STYLES.fontSize.sectionTitle);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('8. PERIOD COVERED BY CERTIFICATION (If applicable)', 
      this.pageState.margin + STYLES.spacing.sectionPadding, startY + 15);
    
    // Add date fields
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(STYLES.fontSize.content);
    this.doc.text('FROM: _____________________          TO: _____________________', 
      this.pageState.margin + STYLES.spacing.sectionPadding * 2, startY + 35);
    
    this.pageState.currentY = startY + sectionHeight;
  }

  // Render certification section with proper formatting
  private static renderCertificationSection(): void {
    const sectionHeight = 180;
    
    this.checkPageBreak(sectionHeight);
    
    const startY = this.pageState.currentY;
    
    // Draw border
    this.doc.setLineWidth(STYLES.dimensions.borderWidth);
    this.doc.rect(this.pageState.margin, startY, this.pageState.contentWidth, sectionHeight);
    
    // Add title
    this.doc.setFontSize(STYLES.fontSize.sectionTitle);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('9. CERTIFICATION', this.pageState.margin + STYLES.spacing.sectionPadding, startY + 15);
    
    // Certification text
    this.doc.setFontSize(STYLES.fontSize.content);
    this.doc.setFont('helvetica', 'normal');
    
    const certificationText = 'I CERTIFY THAT THE GOODS DESCRIBED IN THIS DOCUMENT QUALIFY AS ORIGINATING AND THE ' +
      'INFORMATION CONTAINED IN THIS DOCUMENT IS TRUE AND ACCURATE. I ASSUME RESPONSIBILITY FOR PROVING SUCH ' +
      'REPRESENTATIONS AND AGREE TO MAINTAIN AND PRESENT UPON REQUEST OR TO MAKE AVAILABLE DURING A VERIFICATION ' +
      'VISIT, DOCUMENTATION NECESSARY TO SUPPORT THIS CERTIFICATION.';
    
    const maxWidth = this.pageState.contentWidth - (STYLES.spacing.sectionPadding * 4);
    const certificationLines = this.wrapText(certificationText, maxWidth);
    
    let textY = startY + 35;
    certificationLines.forEach(line => {
      this.doc.text(line, this.pageState.margin + STYLES.spacing.sectionPadding * 2, textY);
      textY += STYLES.spacing.smallLineHeight;
    });
    
    // Page count
    textY += 10;
    this.doc.text(`THIS CERTIFICATION CONSISTS OF ${this.pageState.pageNumber} PAGES, INCLUDING ALL ATTACHMENTS.`,
      this.pageState.margin + STYLES.spacing.sectionPadding * 2, textY);
    
    // Signature section
    textY += 30;
    this.doc.text('SIGNATURE: _______________________________________     DATE: ____________________',
      this.pageState.margin + STYLES.spacing.sectionPadding * 2, textY);
    
    textY += 20;
    this.doc.setFont('helvetica', 'italic');
    this.doc.setFontSize(STYLES.fontSize.small);
    const signatureNote = '(SIGNED BY THE CERTIFIER LISTED ABOVE)';
    const noteWidth = this.doc.getTextWidth(signatureNote);
    const noteX = this.pageState.margin + (this.pageState.contentWidth - noteWidth) / 2;
    this.doc.text(signatureNote, noteX, textY);
    
    this.pageState.currentY = startY + sectionHeight;
  }
}